/**
 * Direct printing to the Jadens JD-268BT — no Jadens app, no driver, no
 * print dialog.
 *
 * The printer speaks TSPL. A label goes over as one image in exactly the form
 * the Windows driver sends it: the driver's SIZE / GAP / SETC framing and a
 * zlib-compressed 1-bit bitmap (`BITMAP x,y,widthBytes,height,3,length,data`,
 * where a 1 bit is a black dot). Two ways to reach it:
 *
 *  - Web Bluetooth (Chrome/Edge on a computer or Android; the Bluefy browser
 *    on iPhone, since Safari has no Bluetooth): the printer's low-energy side,
 *    which is how the Jadens phone app talks to it.
 *  - Web Serial (Chrome/Edge on a computer): the Bluetooth serial port Windows
 *    set up when the printer was paired (COM3 on the seller's laptop).
 *  - The print server: any browser at all. The job goes to the API, and the
 *    Raspberry Pi plugged into the printer picks it up within a second.
 *
 * Whatever the route, the job must stay inside the driver's command subset:
 * this firmware jams on anything else until it is power-cycled.
 */

import { api } from "./api";

export const DPI = 203; // the JD-268BT's print head

/* --- Device API typings (not in TypeScript's DOM lib) ---------------------- */

interface BleCharacteristic {
  uuid: string;
  properties: { write: boolean; writeWithoutResponse: boolean };
  writeValueWithResponse(data: BufferSource): Promise<void>;
  writeValueWithoutResponse(data: BufferSource): Promise<void>;
}
interface BleService {
  uuid: string;
  getCharacteristics(): Promise<BleCharacteristic[]>;
}
interface BleServer {
  getPrimaryServices(): Promise<BleService[]>;
  disconnect(): void;
}
interface BleDevice extends EventTarget {
  name?: string;
  gatt?: { connect(): Promise<BleServer> };
}
interface BluetoothApi {
  requestDevice(options: {
    filters?: Array<{ namePrefix: string }>;
    acceptAllDevices?: boolean;
    optionalServices: string[];
  }): Promise<BleDevice>;
  getDevices?(): Promise<BleDevice[]>;
}
interface SerialPortLike {
  open(options: { baudRate: number }): Promise<void>;
  writable: WritableStream<Uint8Array> | null;
}
interface SerialApi {
  requestPort(): Promise<SerialPortLike>;
  getPorts(): Promise<SerialPortLike[]>;
}

const nav = navigator as Navigator & { bluetooth?: BluetoothApi; serial?: SerialApi };
export const canBluetooth = !!nav.bluetooth;
export const canSerial = !!nav.serial;

/* --- Connections ----------------------------------------------------------- */

export interface PrinterConnection {
  name: string;
  via: "bluetooth" | "serial" | "server";
  send(data: Uint8Array): Promise<void>;
}

// One printer per page session, so every label after the first prints on a tap.
let active: PrinterConnection | null = null;
export function connectedPrinter(): PrinterConnection | null {
  return active;
}

/** The print server: the Raspberry Pi beside the printer. Works from any browser,
 *  needs no pairing, and stays the session's printer once chosen. */
export function connectPrintServer(): PrinterConnection {
  const conn: PrinterConnection = {
    name: "print server",
    via: "server",
    async send(data) {
      await api.post("/print-jobs", { tspl: base64(data) });
    },
  };
  active = conn;
  return conn;
}

function base64(bytes: Uint8Array): string {
  let bin = "";
  for (let i = 0; i < bytes.length; i += 0x8000) {
    bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(bin);
}

const NAME_PREFIXES = ["JD", "Jadens", "JADENS"];
const uuid16 = (hex: string) => `0000${hex}-0000-1000-8000-00805f9b34fb`;
// Web Bluetooth only exposes services named up front, and cheap label printers
// use one of a handful of serial-over-BLE services. Listed in the order to try:
// the common printer ones first, then the two the JD-268BT publishes in its
// Bluetooth service records (FEE7 and the "WE CHAT" 128-bit one).
const PRINTER_SERVICES = [
  uuid16("18f0"),
  uuid16("ff00"),
  uuid16("ffe0"),
  uuid16("ae30"),
  uuid16("ffb0"),
  "49535343-fe7d-4ae5-8fa9-9fafd205e455",
  "e7810a71-73ae-499d-8c15-faa9aef0c3f2",
  uuid16("fee7"),
  "e5b152ed-6b46-09e9-4678-665e9a972cbc",
];

/** Pick the printer from the browser's Bluetooth list. `showAll` lists every
 *  nearby device, for when the printer advertises under another name. */
export async function connectBluetooth(showAll = false): Promise<PrinterConnection> {
  if (!nav.bluetooth) throw new Error("This browser can't use Bluetooth. On iPhone, open the app in Bluefy.");
  const device = await nav.bluetooth.requestDevice(
    showAll
      ? { acceptAllDevices: true, optionalServices: PRINTER_SERVICES }
      : { filters: NAME_PREFIXES.map((namePrefix) => ({ namePrefix })), optionalServices: PRINTER_SERVICES }
  );
  return openBluetooth(device);
}

async function openBluetooth(device: BleDevice): Promise<PrinterConnection> {
  if (!device.gatt) throw new Error("That device doesn't accept Bluetooth connections");
  const server = await device.gatt.connect();
  const services = (await server.getPrimaryServices()).sort(
    (a, b) => PRINTER_SERVICES.indexOf(a.uuid) - PRINTER_SERVICES.indexOf(b.uuid)
  );
  let target: BleCharacteristic | null = null;
  for (const service of services) {
    const chars = await service.getCharacteristics().catch(() => []);
    target =
      chars.find((c) => c.properties.writeWithoutResponse) ??
      chars.find((c) => c.properties.write) ??
      null;
    if (target) break;
  }
  if (!target) {
    server.disconnect();
    throw new Error(`Connected to ${device.name ?? "the device"}, but it offers no way to send a label.`);
  }
  const channel = target;
  const conn: PrinterConnection = {
    name: device.name || "Bluetooth printer",
    via: "bluetooth",
    send: (data) => guarded(conn, () => writeBle(channel, data)),
  };
  device.addEventListener("gattserverdisconnected", () => {
    if (active === conn) active = null;
  });
  active = conn;
  return conn;
}

// 20 bytes is what the smallest Bluetooth packet carries, so it works on every
// phone; a label is ~2KB compressed, a hundred-odd packets. A short pause every
// few hundred bytes keeps a small printer buffer from overflowing.
const BLE_CHUNK = 20;
async function writeBle(c: BleCharacteristic, data: Uint8Array): Promise<void> {
  for (let i = 0; i < data.length; i += BLE_CHUNK) {
    const part = data.slice(i, i + BLE_CHUNK);
    if (c.properties.writeWithoutResponse) await c.writeValueWithoutResponse(part);
    else await c.writeValueWithResponse(part);
    if (i % 400 === 380) await new Promise((r) => setTimeout(r, 15));
  }
}

/** Pick the printer's serial port (on Windows, the paired printer's COM port). */
export async function connectSerial(): Promise<PrinterConnection> {
  if (!nav.serial) throw new Error("This browser can't open serial ports. Use Chrome or Edge on a computer.");
  return openSerial(await nav.serial.requestPort());
}

async function openSerial(port: SerialPortLike): Promise<PrinterConnection> {
  // The rate is ignored by a Bluetooth serial link, but the API requires one.
  await port.open({ baudRate: 9600 }).catch((err: unknown) => {
    if (!(err instanceof DOMException && err.name === "InvalidStateError")) throw err; // already open
  });
  const conn: PrinterConnection = {
    name: "printer on COM port",
    via: "serial",
    send: (data) =>
      guarded(conn, async () => {
        if (!port.writable) throw new Error("The serial port closed");
        const writer = port.writable.getWriter();
        try {
          await writer.write(data);
        } finally {
          writer.releaseLock();
        }
      }),
  };
  active = conn;
  return conn;
}

/** A failed send drops the connection, so the next tap reconnects cleanly. */
async function guarded(conn: PrinterConnection, write: () => Promise<void>): Promise<void> {
  try {
    await write();
  } catch (err) {
    if (active === conn) active = null;
    throw new Error(
      `Lost the printer (${err instanceof Error ? err.message : "send failed"}). Check it's on, then print again.`
    );
  }
}

/** Reopen a printer this browser was allowed before, without showing a picker.
 *  Resolves null when there isn't one, or it isn't in range. */
export async function reconnect(): Promise<PrinterConnection | null> {
  if (active) return active;
  try {
    const [port] = (await nav.serial?.getPorts()) ?? [];
    if (port) return await openSerial(port);
  } catch { /* fall through to Bluetooth */ }
  try {
    const devices = (await nav.bluetooth?.getDevices?.()) ?? [];
    const device = devices.find((d) => NAME_PREFIXES.some((p) => d.name?.startsWith(p)));
    if (device) return await openBluetooth(device);
  } catch { /* not in range; the next Print will ask */ }
  return null;
}

/* --- TSPL ------------------------------------------------------------------- */

const ascii = (s: string) => new TextEncoder().encode(s);

async function zlib(data: Uint8Array<ArrayBuffer>): Promise<Uint8Array> {
  if (typeof CompressionStream === "undefined") {
    throw new Error("This browser is too old to print labels directly. Update it and try again.");
  }
  const stream = new Blob([data]).stream().pipeThrough(new CompressionStream("deflate"));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

/**
 * The drawn label as a TSPL job. `canvas` is the label at 203 dpi, its width a
 * multiple of 8; anything darker than mid-grey becomes a dot.
 */
export async function tsplJob(canvas: HTMLCanvasElement, widthIn: number, heightIn: number): Promise<Uint8Array> {
  const { width, height } = canvas;
  const px = canvas.getContext("2d")!.getImageData(0, 0, width, height).data;
  const widthBytes = width / 8;
  const bits = new Uint8Array(widthBytes * height);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      if (px[i] + px[i + 1] + px[i + 2] < 384) bits[y * widthBytes + (x >> 3)] |= 0x80 >> (x & 7);
    }
  }
  const image = await zlib(bits);
  const mm = (inches: number) => (inches * 25.4).toFixed(2);
  const head = [
    `SIZE ${mm(widthIn)} mm,${mm(heightIn)} mm`,
    "REFERENCE 0,0",
    "GAP 24 dot,0 dot", // 3 mm between labels, as the driver sends
    "OFFSET 0 dot",
    "SETC AUTODOTTED OFF",
    "SETC PAUSEKEY ON",
    "SETC WATERMARK OFF",
    "CLS",
    `BITMAP 0,0,${widthBytes},${height},3,${image.length},`,
  ].join("\r\n");
  const tail = ascii("\r\nPRINT 1,1\r\n");
  const headBytes = ascii(head);
  const job = new Uint8Array(headBytes.length + image.length + tail.length);
  job.set(headBytes, 0);
  job.set(image, headBytes.length);
  job.set(tail, headBytes.length + image.length);
  return job;
}
